import { useCallback, useEffect, useRef, useState } from "react";
import { createSharedVolume, type SharedVolume } from "@/lib/api";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { TextInputField } from "@/components/common/TextInputField";

interface CreateSharedVolumeDialogProps {
	open: boolean;
	onClose: () => void;
	onCreated: (volume: SharedVolume) => void;
}

/**
 * 共有ボリュームの作成ダイアログ。入力は名前のみ (容量は無制限・実費課金のため選ばない)。
 * 名前はマウント先ディレクトリ名 (/mnt/volumes/<名前>) になる。
 */
export function CreateSharedVolumeDialog({
	open,
	onClose,
	onCreated,
}: CreateSharedVolumeDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) el.showModal();
		else if (!open && el.open) el.close();
	}, [open]);

	useEffect(() => {
		if (open) {
			setName("");
			setError(null);
			setSubmitError(null);
		}
	}, [open]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				if (!loading) onClose();
			}
		},
		[onClose, loading],
	);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) {
			setError("ボリューム名は必須です。");
			return;
		}
		if (/[/\\:]/.test(trimmed) || trimmed.startsWith(".")) {
			setError("ボリューム名に / や \\ や : は使えません。先頭の「.」も使えません。");
			return;
		}
		setError(null);
		setSubmitError(null);
		setLoading(true);
		try {
			const created = await createSharedVolume(trimmed);
			onCreated(created);
			onClose();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="create-shared-volume-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-popover)",
				background: "var(--surface-1)",
				color: "var(--fg-1)",
				width: 480,
			}}
			className="backdrop:bg-scrim"
		>
			<form onSubmit={handleSubmit} noValidate>
				<div style={{ padding: "16px 24px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
						}}
					>
						共有ボリュームを作成
					</h3>
				</div>
				<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					<TextInputField
						label="ボリューム名"
						value={name}
						onChange={setName}
						placeholder="例: datasets"
						error={error ?? undefined}
					/>
					<span
						style={{
							fontSize: "var(--text-caption)",
							color: "var(--fg-2)",
							fontFamily: "var(--font-sans)",
						}}
					>
						エージェントには /mnt/volumes/{name.trim() || "<名前>"}{" "}
						としてマウントされます。容量の上限はなく、 使用した分だけ組織に課金されます。
					</span>
					{submitError && (
						<div
							data-testid="create-shared-volume-error"
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{submitError}
						</div>
					)}
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						padding: "12px 24px",
						borderTop: "var(--pane-header-border)",
					}}
				>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={loading}
						onClick={onClose}
					/>
					<ConfirmButton
						type="submit"
						label="作成"
						loading={loading}
						disabled={!name.trim()}
						data-testid="create-shared-volume-submit"
					/>
				</div>
			</form>
		</dialog>
	);
}
